// Lightweight in-memory query cache (no persistence: private data never touches storage).
// Keys are always scoped by user id, and the whole cache is wiped on logout / account switch.
const store = new Map(); // key -> { data, updatedAt, error, promise, invalidated }
const listeners = new Set();
let generation = 0; // bumped on clear so late responses from a previous session are discarded
let currentUserId = null;

const notify = () => listeners.forEach((l) => l());
export const subscribe = (l) => { listeners.add(l); return () => listeners.delete(l); };

export const makeKey = (userId, ...parts) => JSON.stringify([userId, ...parts]);
export const getEntry = (key) => store.get(key);

export const clearCache = () => {
  generation += 1;
  store.clear();
  notify();
};

// Call whenever the authenticated user changes. Different user => drop everything.
export const setCacheUser = (userId) => {
  const next = userId || null;
  if (next !== currentUserId) {
    currentUserId = next;
    clearCache();
  }
};

export const fetchQuery = (key, fetcher, { staleTime = 0, force = false } = {}) => {
  const entry = store.get(key);
  if (entry?.promise) return entry.promise; // dedupe in-flight requests
  const fresh = entry && entry.data !== undefined && !entry.invalidated
    && Date.now() - entry.updatedAt < staleTime;
  if (fresh && !force) return Promise.resolve(entry.data);

  const gen = generation;
  const promise = Promise.resolve()
    .then(fetcher)
    .then((data) => {
      if (gen !== generation) return data; // cache was cleared meanwhile: drop result
      store.set(key, { data, updatedAt: Date.now(), error: null, promise: null, invalidated: false });
      notify();
      return data;
    })
    .catch((error) => {
      if (gen === generation) {
        // keep previously valid data, only record the error
        const prev = store.get(key) || {};
        store.set(key, { ...prev, error, promise: null });
        notify();
      }
      throw error;
    });
  store.set(key, { ...(entry || { data: undefined, updatedAt: 0 }), error: null, promise });
  notify();
  return promise;
};

export const setQueryData = (key, updater) => {
  const prev = store.get(key);
  const data = typeof updater === 'function' ? updater(prev?.data) : updater;
  store.set(key, { ...(prev || {}), data, updatedAt: Date.now(), error: null, promise: prev?.promise || null, invalidated: false });
  notify();
};

// Mark matching entries stale; mounted hooks refetch automatically.
export const invalidateQueries = (predicate) => {
  store.forEach((entry, key) => {
    let parts;
    try { parts = JSON.parse(key); } catch { return; }
    if (predicate(parts)) store.set(key, { ...entry, invalidated: true });
  });
  notify();
};
